import React, { useEffect, useState } from "react";
import {
  User,
  Shield,
  CheckCircle,
  XCircle,
  Plus,
  Edit,
  Trash2,
  Package,
} from "lucide-react";
import { supabase } from "../../supabaseClient";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "../../components/ui/alert-dialog";

const DeleteProductAlert = ({ onConfirm, description, idPrefix }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button
        id={idPrefix && `${idPrefix}-btn-delete`}
        variant="ghost"
        size="icon"
        title="Excluir"
        className="text-red-400 hover:bg-red-50 hover:text-red-500"
      >
        <Trash2 size={16} />
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Excluir?</AlertDialogTitle>
        <AlertDialogDescription>
          {description || "Esta ação não pode ser desfeita."}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction variant="destructive" onClick={onConfirm}>
          Excluir
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const SubscriptionCardListItem = ({ assinatura, onEdit, onDelete }) => {
  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const getStatusInfo = () => {
    if (assinatura.ativa) {
      if (isExpired(assinatura.data_fim)) {
        return { text: "Expirada", color: "#f59e0b" };
      }
      return { text: "Ativa", color: "#10b981" };
    }
    return { text: assinatura.status_pagamento || "Inativa", color: "#ef4444" };
  };

  const status = getStatusInfo();

  return (
    <Card id={`admin-sub-card-item-${assinatura.id}`}>
      <CardContent className="flex items-center gap-3 p-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
          <User size={22} className="text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-[2]">
          <p className="truncate text-sm font-semibold text-foreground">
            {assinatura.usuario_nome} {assinatura.usuario_sobrenome}
          </p>
          <p className="truncate text-xs text-muted-foreground">{assinatura.produto_nome}</p>
        </div>
        <div className="hidden flex-[2] flex-col gap-0.5 text-[11px] text-muted-foreground sm:flex">
          <span>
            Início: {assinatura.data_inicio ? new Date(assinatura.data_inicio).toLocaleDateString() : "-"}
          </span>
          <span>Fim: {assinatura.data_fim ? new Date(assinatura.data_fim).toLocaleDateString() : "-"}</span>
          <span className="font-semibold" style={{ color: status.color }}>
            {status.text}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            id={`admin-sub-card-btn-edit-${assinatura.id}`}
            variant="ghost"
            size="icon"
            onClick={() => onEdit(assinatura)}
            title="Editar"
            className="text-secondary hover:bg-secondary/10"
          >
            <Edit size={16} />
          </Button>
          <DeleteProductAlert
            idPrefix={`admin-sub-card-${assinatura.id}`}
            description="Esta assinatura/venda será excluída permanentemente."
            onConfirm={() => onDelete(assinatura.id)}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const SubscriptionPlanCardListItem = ({ plano, onEdit, onDelete, onToggleStatus }) => {
  return (
    <Card id={`admin-plan-card-item-${plano.id}`}>
      <CardContent className="flex items-center gap-3 p-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
          <Shield size={22} className="text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-[2]">
          <p className="truncate text-sm font-semibold text-foreground">{plano.nome}</p>
          <p className="text-xs text-muted-foreground">
            {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(plano.valor || 0)} /mês
          </p>
        </div>
        <div className="hidden flex-1 sm:flex">
          <Badge variant={plano.ativo ? "secondary" : "outline"}>
            {plano.ativo ? "Ativo" : "Inativo"}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            id={`admin-plan-card-btn-toggle-${plano.id}`}
            variant="ghost"
            size="icon"
            onClick={() => onToggleStatus(plano.id, !plano.ativo)}
            title={plano.ativo ? "Desativar" : "Ativar"}
            className={plano.ativo ? "text-red-400 hover:bg-red-50" : "text-emerald-500 hover:bg-emerald-50"}
          >
            {plano.ativo ? <XCircle size={16} /> : <CheckCircle size={16} />}
          </Button>
          <Button
            id={`admin-plan-card-btn-edit-${plano.id}`}
            variant="ghost"
            size="icon"
            onClick={() => onEdit(plano)}
            title="Editar"
            className="text-secondary hover:bg-secondary/10"
          >
            <Edit size={16} />
          </Button>
          <DeleteProductAlert
            idPrefix={`admin-plan-card-${plano.id}`}
            description="Este plano será excluído permanentemente."
            onConfirm={() => onDelete(plano.id)}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const PackageCardListItem = ({ pacote, onEdit, onDelete, onToggleStatus }) => {
  return (
    <Card id={`admin-pkg-card-item-${pacote.id}`}>
      <CardContent className="flex items-center gap-3 p-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
          <Package size={22} className="text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-[2]">
          <p className="truncate text-sm font-semibold text-foreground">{pacote.nome}</p>
          <p className="text-xs text-muted-foreground">
            {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(pacote.valor || 0)}
            {pacote.horas ? ` • ${pacote.horas} horas` : ""}
          </p>
        </div>
        <div className="hidden flex-1 sm:flex">
          <Badge variant={pacote.ativo ? "secondary" : "outline"}>
            {pacote.ativo ? "Ativo" : "Inativo"}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            id={`admin-pkg-card-btn-toggle-${pacote.id}`}
            variant="ghost"
            size="icon"
            onClick={() => onToggleStatus(pacote.id, !pacote.ativo)}
            title={pacote.ativo ? "Desativar" : "Ativar"}
            className={pacote.ativo ? "text-red-400 hover:bg-red-50" : "text-emerald-500 hover:bg-emerald-50"}
          >
            {pacote.ativo ? <XCircle size={16} /> : <CheckCircle size={16} />}
          </Button>
          <Button
            id={`admin-pkg-card-btn-edit-${pacote.id}`}
            variant="ghost"
            size="icon"
            onClick={() => onEdit(pacote)}
            title="Editar"
            className="text-secondary hover:bg-secondary/10"
          >
            <Edit size={16} />
          </Button>
          <DeleteProductAlert
            idPrefix={`admin-pkg-card-${pacote.id}`}
            description="Este pacote será excluído permanentemente."
            onConfirm={() => onDelete(pacote.id)}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default function AssinaturasAdminContent() {
  const [assinaturas, setAssinaturas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAssinatura, setEditingAssinatura] = useState(null);
  const [activeTab, setActiveTab] = useState("planos");

  const [showPlanosManager, setShowPlanosManager] = useState(false);
  const [editingPlanoId, setEditingPlanoId] = useState(null);
  const [newPlano, setNewPlano] = useState({
    nome: "",
    valor: "",
    descricao: {
      chamada: "",
      texto: "",
      beneficios: [],
      ideal_para: "",
    },
  });

  const [formData, setFormData] = useState({
    user_id: "",
    plano_id: "",
    status: "approved",
    data_inicio: "",
    data_fim: "",
    renovacao_automatica: true,
    ativa: true,
    data_compra: "",
  });

  useEffect(() => {
    fetchAssinaturas();
    fetchPlanos();
    fetchPacotes();
    fetchUsers();
  }, []);

  const fetchAssinaturas = async () => {
    try {
      const { data: assinaturasData, error: assinaturasError } = await supabase
        .from("vw_historico_vendas")
        .select("*");

      if (assinaturasError) throw assinaturasError;
      setAssinaturas(assinaturasData || []);
    } catch (error) {
      console.error("Erro ao buscar assinaturas:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanos = async () => {
    try {
      const { data, error } = await supabase
        .from("produtos")
        .select("*")
        .eq("tipo", "assinatura")
        .order("valor", { ascending: true });

      if (error) throw error;
      setPlanos(data || []);
    } catch (error) {
      console.error("Erro ao buscar planos:", error);
    }
  };

  const fetchPacotes = async () => {
    try {
      const { data, error } = await supabase
        .from("produtos")
        .select("*")
        .eq("tipo", "pacote_horas")
        .order("valor", { ascending: true });
      if (error) throw error;
      setPacotes(data || []);
    } catch (error) {
      console.error("Erro ao buscar pacotes:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await supabase
        .from("usuarios")
        .select("id, nome, sobrenome, tipo")
        .order("nome");
      setUsers(data || []);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
    }
  };

  const handleSavePlano = async (e) => {
    e.preventDefault();
    try {
      const planoData = newPlano;

      if (editingPlanoId) {
        const { error } = await supabase
          .from("produtos")
          .update(planoData)
          .eq("id", editingPlanoId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("produtos").insert([
          {
            ...planoData,
            ativo: true,
          },
        ]);
        if (error) throw error;
      }

      alert("Plano criado com sucesso!");
      setNewPlano({
        nome: "",
        valor: "",
        descricao: {
          chamada: "",
          texto: "",
          beneficios: [],
          ideal_para: "",
        },
      });
      setEditingPlanoId(null);
      await fetchPlanos();
      await fetchPacotes();
      setShowPlanosManager(false);
    } catch (error) {
      console.error("Erro ao salvar plano:", error);
      alert("Erro ao salvar plano: " + error.message);
    }
  };

  // Confirmação já é feita pelo AlertDialog (DeleteProductAlert) na UI antes de chamar este handler.
  const handleDeletePlano = async (id) => {
    try {
      const { error } = await supabase.from("produtos").delete().eq("id", id);
      if (error) throw error;
      await fetchPlanos();
      await fetchPacotes();
    } catch (error) {
      console.error("Erro ao deletar plano:", error);
      alert("Erro ao deletar plano.");
    }
  };

  const handleTogglePlanoStatus = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from("produtos")
        .update({ ativo: newStatus })
        .eq("id", id);
      if (error) throw error;
      await fetchPlanos();
      await fetchPacotes();
    } catch (error) {
      console.error("Erro ao atualizar status do plano:", error);
      alert("Erro ao atualizar status.");
    }
  };

  const startEditPlano = (plano) => {
    let desc = { chamada: "", texto: "", beneficios: [], ideal_para: "" };
    if (plano.descricao) {
      if (typeof plano.descricao === "string") {
        desc.texto = plano.descricao;
      } else {
        desc = {
          ...desc,
          ...plano.descricao,
          beneficios: Array.isArray(plano.descricao.beneficios)
            ? plano.descricao.beneficios
            : [],
        };
      }
    }

    setNewPlano({ ...plano, descricao: desc });
    setEditingPlanoId(plano.id);
    setShowPlanosManager(true);
  };

  const handleUpdate = async () => {
    try {
      const isAssinatura = editingAssinatura.tipo === "assinatura";

      const updateData = {
        user_id: formData.user_id,
        produto_id: formData.plano_id,
        status_pagamento: formData.status,
        ativa: formData.ativa,
      };

      if (isAssinatura) {
        updateData.data_inicio = formData.data_inicio;
        updateData.data_fim = formData.data_fim;
        updateData.renovacao_automatica = formData.renovacao_automatica;
      } else {
        updateData.data_compra = formData.data_compra;
      }

      const { error } = await supabase
        .from("vendas")
        .update(updateData)
        .eq("id", editingAssinatura.id);

      if (error) throw error;

      if (isAssinatura && formData.ativa) {
        await supabase
          .from("usuarios")
          .update({ data_fim: formData.data_fim })
          .eq("id", formData.user_id);
      }

      fetchAssinaturas();
      setEditingAssinatura(null);
      resetForm();
    } catch (error) {
      console.error("Erro ao atualizar assinatura:", error);
      alert("Erro ao atualizar assinatura");
    }
  };

  // Confirmação já é feita pelo AlertDialog (DeleteProductAlert) na UI antes de chamar este handler.
  const handleDelete = async (id) => {

    try {
      const { error } = await supabase.from("vendas").delete().eq("id", id);

      if (error) throw error;
      fetchAssinaturas();
    } catch (error) {
      console.error("Erro ao deletar assinatura:", error);
      alert("Erro ao deletar assinatura");
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      plano_id: "",
      status: "approved",
      data_inicio: "",
      data_fim: "",
      renovacao_automatica: true,
      ativa: true,
      data_compra: "",
    });
  };

  const startEdit = (assinatura) => {
    setEditingAssinatura(assinatura);
    setFormData({
      user_id: assinatura.user_id,
      plano_id: assinatura.produto_id,
      status: assinatura.status_pagamento || "approved",
      data_inicio: assinatura.data_inicio ? assinatura.data_inicio.split("T")[0] : "",
      data_fim: assinatura.data_fim ? assinatura.data_fim.split("T")[0] : "",
      renovacao_automatica: assinatura.renovacao_automatica ?? false,
      ativa: assinatura.ativa ?? false,
      data_compra: assinatura.data_compra ? assinatura.data_compra.split("T")[0] : "",
    });
  };

  const filteredUsers = users;

  if (loading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">Carregando...</div>;
  }

  const lbl = "mb-1 block text-xs font-semibold text-muted-foreground";

  return (
    <div className="space-y-5">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="planos">Gerenciar Assinaturas</TabsTrigger>
          <TabsTrigger value="pacotes">Planos de Horas</TabsTrigger>
          <TabsTrigger value="assinaturas">Produtos Ativos</TabsTrigger>
        </TabsList>
      </Tabs>

      <Dialog
        open={showPlanosManager}
        onOpenChange={(open) => {
          if (!open) {
            setShowPlanosManager(false);
            setEditingPlanoId(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlanoId ? "Editar Plano" : "Criar Novo Plano"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePlano} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className={lbl}>Nome do Plano</Label>
                <Input
                  placeholder="Ex: Plano Mensal"
                  value={newPlano.nome}
                  onChange={(e) => setNewPlano({ ...newPlano, nome: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className={lbl}>Preço (R$)</Label>
                <Input
                  placeholder="0.00"
                  type="number"
                  step="0.01"
                  value={newPlano.valor}
                  onChange={(e) => setNewPlano({ ...newPlano, valor: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Chamada (Destaque)</Label>
              <Input
                placeholder="Ex: Para famílias que precisam de apoio"
                value={newPlano.descricao?.chamada || ""}
                onChange={(e) =>
                  setNewPlano({
                    ...newPlano,
                    descricao: { ...newPlano.descricao, chamada: e.target.value },
                  })
                }
              />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Descrição Detalhada</Label>
              <textarea
                placeholder="Ex: Pontual com segurança e praticidade."
                value={newPlano.descricao?.texto || ""}
                onChange={(e) =>
                  setNewPlano({
                    ...newPlano,
                    descricao: { ...newPlano.descricao, texto: e.target.value },
                  })
                }
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                rows={2}
              />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Benefícios (Um por linha)</Label>
              <textarea
                placeholder={"Ex: Acesso ao banco...\nPerfis com avaliações..."}
                value={
                  Array.isArray(newPlano.descricao?.beneficios)
                    ? newPlano.descricao.beneficios.join("\n")
                    : ""
                }
                onChange={(e) =>
                  setNewPlano({
                    ...newPlano,
                    descricao: {
                      ...newPlano.descricao,
                      beneficios: e.target.value.split("\n"),
                    },
                  })
                }
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                rows={4}
              />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Ideal Para</Label>
              <textarea
                placeholder="Ex: Pais que precisam de baby-sitter ocasionalmente..."
                value={newPlano.descricao?.ideal_para || ""}
                onChange={(e) =>
                  setNewPlano({
                    ...newPlano,
                    descricao: { ...newPlano.descricao, ideal_para: e.target.value },
                  })
                }
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowPlanosManager(false);
                  setEditingPlanoId(null);
                  setNewPlano({
                    nome: "",
                    valor: "",
                    descricao: { chamada: "", texto: "", beneficios: [], ideal_para: "" },
                  });
                }}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingPlanoId ? "Salvar Alterações" : "Salvar Novo Plano"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editingAssinatura}
        onOpenChange={(open) => {
          if (!open) {
            setEditingAssinatura(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {editingAssinatura && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {editingAssinatura.tipo === "assinatura"
                    ? "Editar Assinatura"
                    : "Editar Pacote de Horas"}
                </DialogTitle>
              </DialogHeader>
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdate();
                }}
              >
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className={lbl}>Usuário</Label>
                    <Select
                      value={formData.user_id}
                      onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione um usuário" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredUsers.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.nome} {u.sobrenome} ({u.tipo})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className={lbl}>{editingAssinatura.tipo === "assinatura" ? "Plano" : "Pacote"}</Label>
                    <Select
                      value={formData.plano_id}
                      onValueChange={(value) => setFormData({ ...formData, plano_id: value })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(editingAssinatura.tipo === "assinatura" ? planos : pacotes).map((plano) => (
                          <SelectItem key={plano.id} value={plano.id}>
                            {plano.nome} - R$ {plano.valor}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {editingAssinatura.tipo === "assinatura" ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className={lbl}>Data Início</Label>
                        <Input
                          type="date"
                          value={formData.data_inicio}
                          onChange={(e) => {
                            const inicio = new Date(e.target.value);
                            const fim = new Date(inicio);
                            fim.setDate(fim.getDate() + 30);
                            setFormData({
                              ...formData,
                              data_inicio: e.target.value,
                              data_fim: fim.toISOString().split("T")[0],
                            });
                          }}
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className={lbl}>Data Fim</Label>
                        <Input
                          type="date"
                          value={formData.data_fim}
                          onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className={lbl}>Status Pagamento</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData({ ...formData, status: value })}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="approved">Aprovado</SelectItem>
                            <SelectItem value="pending">Pendente</SelectItem>
                            <SelectItem value="cancelled">Cancelado</SelectItem>
                            <SelectItem value="rejected">Recusado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          id="renovacao-auto"
                          checked={formData.renovacao_automatica}
                          onChange={(e) =>
                            setFormData({ ...formData, renovacao_automatica: e.target.checked })
                          }
                          className="h-4 w-4 rounded border-input accent-primary"
                        />
                        <Label htmlFor="renovacao-auto" className="cursor-pointer">
                          Renovação Automática
                        </Label>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className={lbl}>Data da Compra</Label>
                      <Input
                        type="date"
                        value={formData.data_compra}
                        onChange={(e) => setFormData({ ...formData, data_compra: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className={lbl}>Status Pagamento</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="approved">Aprovado</SelectItem>
                          <SelectItem value="pending">Pendente</SelectItem>
                          <SelectItem value="cancelled">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="registro-ativo"
                    checked={formData.ativa}
                    onChange={(e) => setFormData({ ...formData, ativa: e.target.checked })}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <Label htmlFor="registro-ativo" className="cursor-pointer">
                    Registro Ativo no Sistema?
                  </Label>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditingAssinatura(null);
                      resetForm();
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit">Salvar Alterações</Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {activeTab === "planos" && (
        <div id="admin-plans-list-pane" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-foreground">Assinaturas Disponíveis</h2>
            <Button
              onClick={() => {
                setNewPlano({
                  nome: "",
                  valor: "",
                  descricao: { chamada: "", texto: "", beneficios: [], ideal_para: "" },
                  tipo: "assinatura",
                });
                setEditingPlanoId(null);
                setShowPlanosManager(true);
              }}
            >
              <Plus size={16} /> Novo Plano
            </Button>
          </div>
          <div id="admin-plans-list-wrapper" className="flex flex-col gap-3">
            {planos.length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhuma assinatura cadastrada.</p>
            )}
            {[...planos]
              .sort((a, b) => (a.ativo === b.ativo ? 0 : a.ativo ? -1 : 1))
              .map((plano) => (
                <SubscriptionPlanCardListItem
                  key={plano.id}
                  plano={plano}
                  onEdit={startEditPlano}
                  onDelete={handleDeletePlano}
                  onToggleStatus={handleTogglePlanoStatus}
                />
              ))}
          </div>
        </div>
      )}

      {activeTab === "pacotes" && (
        <div id="admin-packages-list-pane" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-foreground">Pacotes Disponíveis</h2>
            <Button
              onClick={() => {
                setNewPlano({
                  nome: "",
                  valor: "",
                  descricao: { chamada: "", texto: "", beneficios: [], ideal_para: "" },
                  tipo: "pacote_horas",
                });
                setEditingPlanoId(null);
                setShowPlanosManager(true);
              }}
            >
              <Plus size={16} /> Novo Pacote
            </Button>
          </div>
          <div id="admin-packages-list-wrapper" className="flex flex-col gap-3">
            {pacotes.length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhum pacote cadastrado.</p>
            )}
            {[...pacotes]
              .sort((a, b) => (a.ativo === b.ativo ? 0 : a.ativo ? -1 : 1))
              .map((pacote) => (
                <PackageCardListItem
                  key={pacote.id}
                  pacote={pacote}
                  onEdit={startEditPlano}
                  onDelete={handleDeletePlano}
                  onToggleStatus={handleTogglePlanoStatus}
                />
              ))}
          </div>
        </div>
      )}

      {activeTab === "assinaturas" && (
        <div id="admin-subscriptions-list-pane" className="space-y-4">
          <h2 className="text-lg font-bold text-foreground">Histórico de Assinaturas e Vendas</h2>
          <div className="flex flex-col gap-3">
            {assinaturas.length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhuma assinatura ou venda encontrada.</p>
            )}
            {assinaturas.map((assinatura) => (
              <SubscriptionCardListItem
                key={assinatura.id}
                assinatura={assinatura}
                onEdit={startEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
